import webapi from "./fetch";
import { getUserProfile, GetUserProfileResp } from "litb-header/lib/services/header/header";
import { WebapiOptions } from "litb-header/lib/services/webapi";
import { CommonRuntime, Platform, getRuntime } from "./runtime";
import { UserInfos } from "services/page/app";
import { isLocalDevelop } from "ts-constants";
import { isServer } from "./env";

const md5 = require("md5-node");

/** 领取型优惠券 ReceiveCoupon  */
interface CouponsParams {
	isUat: string;
	language: string;
	currency: string;
	country: string;
	sid: string;
	sessionkey: string;
	campaign_key: string;
}

const API_UAT = (runtime: Partial<CommonRuntime>) => {
	let { hostname } = runtime || getRuntime();
	if (!isServer) {
		hostname = location.host;
	}
	if (isLocalDevelop(hostname)) {
		return "https://kdev0.tbox.me";
	}
	try {
		return (location && location.origin) || `https://${hostname}`;
	} catch (error) {
		return `https://${hostname}`;
	}
};

const API_ONLINE = "https://pro.lightinthebox.com";

const transformAddress = (
	data: Partial<CommonRuntime & { isOnline: boolean; isMobile: boolean }>,
) => {
	const { isOnline, isMobile, ...runtime } = data;
	return `${isOnline ? API_ONLINE : API_UAT(runtime)}/litb-header-zeus/${
		isMobile ? "mweb" : "web"
	}`;
};

interface CouponsResp<T> {
	code: string;
	error_msg: any[];
	extInfo: any[];
	result: string;
	info: T;
}

export interface GetReceiveCouponResp extends CouponsResp<{ status: number }> {}

export function getCategories(params: CouponsParams, options): Promise<GetReceiveCouponResp> {
	return webapi<GetReceiveCouponResp>(
		"api/v1/rewardcredits/promotion/getCoupons",
		{ ...params },
		options,
	);
}

export const getGetCoupons = (
	params: CouponsParams,
	isMobile: boolean,
	isUat: boolean,
): Promise<GetReceiveCouponResp> => {
	const options = {
		fetchMethod: "GET",
		apiDomain: transformAddress({
			isOnline: !isUat,
			isMobile,
		}),
	};
	try {
		return getCategories(params, options);
	} catch (error) {
		console.log("getGetCoupons error", error);
	}
};

interface CustomerCouponParams extends CouponsParams {
	client?: string;
	app_key?: string;
	app_secret?: string;
	sign?: string;
}

/**
 * 领取型优惠券领取状态
 */
export enum ReceivedCouponStatusEnum {
	OVERSTEP = 3, // 超出campaign的最大领取数量
	NON_EXISTENT = -1, // campaign不存在
	BE_CLAIMED = 2, // 已经被全部领取完
	AVAILABLE = 1, // 还可以领取
}

export interface ReceivedCouponInfo {
	status: ReceivedCouponStatusEnum;
	maxTimes: number;
}

export interface CustomerCouponResp extends CouponsResp<ReceivedCouponInfo> {}
// 获取用户领取型优惠券信息
export function getCustomerCouponInfo(
	params: Omit<CustomerCouponParams, "isUat">,
	runtime: CommonRuntime,
) {
	const isMobile = runtime.platform !== Platform.WEB;
	const isUat = runtime.isUat;

	const options = {
		fetchMethod: "GET" as const,
		apiDomain: transformAddress({
			isOnline: !isUat,
			isMobile,
			hostname: runtime.hostname,
		}),
	};
	if (isUat) {
		params = {
			...params,
			isUat: "true", // zeus内部也是按照string来校验的
		} as any;
	}
	return webapi<CustomerCouponResp>(
		"api/v1/rewardcredits/promotion/hasCustomerCoupons",
		params,
		options,
	);
}

// https://pro.lightinthebox.com/litb-header-zeus/web/api/v1/rest.app/vela/GetUserProfile?sessionkey=ab7930a73413a362758dcebc11ce0ae9&sid=tbgvn4fgrfg51ckcm893el4imp&language=en&currency=USD&country=USA
export const getUserInfo = async (runtime: CommonRuntime, { onSentryCatch }: WebapiOptions) => {
	const { sessionKey, sid, languageCode, countryCode3, currency } = runtime;

	try {
		const userInfo: GetUserProfileResp = await getUserProfile(
			{
				sessionkey: sessionKey,
				sid,
				language: languageCode,
				country: countryCode3,
				currency,
			},
			{ onSentryCatch, fetchMethod: "GET" },
		);
		return userInfo?.info?.user;
	} catch (error) {
		console.log("getUserProfile error", error);
	}
};

// 获取GetProduct参数userinfo👇
enum ConfigKey {
	Client = "client",
	AppKey = "app_key",
	AppSecret = "app_secret",
}

export const getMd5Sign = (props: { app_key: string; app_secret: string }) => {
	const { app_key, app_secret } = props;
	return md5(`${ConfigKey.AppKey}${app_key}${ConfigKey.AppSecret}${app_secret}`);
};

export const getVisit = (pfLst: string) => {
	return pfLst
		? pfLst.split("+").map((ele) => {
				const eleArr = ele.split("-") || [];
				return {
					["catId"]: eleArr?.[0] || "",
					["times"]: eleArr?.[1] || "",
					["visitTime"]: eleArr?.[2]?.replace(".", "_") || "",
				};
		  })
		: [];
};

/**
 *
 * @param cookies
 * @returns
 * visit 浏览商品记录
 * fav 点击商品收藏时候记录
 * cust 用户信息
 */
export const getUserVistInfo = (
	cookies: {
		[key: string]: string;
	},
	isInApp: boolean,
): {
	visit: string;
	fav: string;
	cust: string;
} => {
	const { favList = "", __cust = "", pfLst = "", visitList = "" } = cookies;

	const visit = isInApp ? visitList : getVisit(pfLst);
	return {
		visit: encodeURIComponent(JSON.stringify(visit)),
		fav: favList,
		cust: __cust,
	};
};
/**
 * https://www.tapd.cn/20404221/markdown_wikis/show/#1120404221001020100
 * @param runtime
 * @returns
 */
export const getGetProductsParams = (runtime: Partial<CommonRuntime>): UserInfos => {
	const { sid, sessionKey, cookies, appKey, appSecret, isInApp, userType } = runtime;

	const { visit, fav, cust } = getUserVistInfo(cookies, isInApp);
	return {
		app_key: appKey,
		app_secret: appSecret,
		sid: sid,
		sessionkey: sessionKey,
		visit,
		fav,
		cust,
		sign: getMd5Sign({ app_key: appKey, app_secret: appSecret }),
		userType: Number(userType),
	};
};
